import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationsDropdown from "./NotificationsDropdown";
import TierBadge from "./TierBadge";
import BetaBanner from "./BetaBanner";
import VerifyEmailBanner from "./VerifyEmailBanner";
import CompleteProfileBanner from "./CompleteProfileBanner";
import useTier from "../../hooks/useTier";
import { Activity, BarChart3, FilePlus2, FileSearch, LayoutGrid, LogOut, ShoppingCart, Users, Wrench, CreditCard, ShieldAlert, Sun, Moon, TrendingDown, Settings, Monitor, Menu, X } from "lucide-react";
import BrandIcon from "./BrandIcon";

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tier, limits, usage, loading: tierLoading } = useTier();
  // Below lg the sidebar is an off-canvas drawer: on a 390px screen a fixed
  // w-64 rail left ~130px of usable width and broke every page.
  const [navOpen, setNavOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const linkBase = "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all duration-150";
  const linkActive = "bg-[#FF4500]/10 text-[#FF6A33] border-l-2 border-[#FF4500]";
  const linkInactive = "text-slate-400 hover:text-slate-100 hover:bg-slate-800";
  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen flex bg-slate-900">
      {navOpen && (
        <button
          type="button"
          aria-label="close navigation"
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-30 bg-black/60 lg:hidden"
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={`w-64 shrink-0 border-r border-slate-800 bg-slate-950 flex flex-col fixed inset-y-0 left-0 z-40 transition-transform duration-200 lg:static lg:translate-x-0 ${
          navOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="px-5 py-5 border-b border-slate-800 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <BrandIcon size={32} />
            <div>
              <div className="font-display font-bold text-slate-100 leading-none">{t("app.name")}</div>
              <div className="label-tech mt-1 text-[10px]">CAM ESTIMATOR v1.0</div>
            </div>
          </Link>
          <NotificationsDropdown/>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto" onClick={() => setNavOpen(false)}>
          <NavLink to="/dashboard" className={({isActive}) => `${linkBase} ${isActive?linkActive:linkInactive}`} data-testid="nav-dashboard"><LayoutGrid size={16}/> {t("nav.dashboard")}</NavLink>
          <NavLink to="/new-quote" className={({isActive}) => `${linkBase} ${isActive?linkActive:linkInactive}`} data-testid="nav-new-quote"><FilePlus2 size={16}/> {t("nav.newQuote")}</NavLink>
          <NavLink to="/import-time-study" className={({isActive}) => `${linkBase} ${isActive?linkActive:linkInactive}`} data-testid="nav-import-ts"><FileSearch size={16}/> {t("nav.importTimeStudy")}</NavLink>

          <div className="pt-2 pb-1 px-3 label-tech text-[10px] text-slate-600">{t("nav.groupWorkshop", "Officina")}</div>
          <NavLink to="/machines" className={({isActive}) => `${linkBase} ${isActive?linkActive:linkInactive}`} data-testid="nav-machines"><Wrench size={16}/> {t("nav.machines")}</NavLink>
          <NavLink to="/calibration" className={({isActive}) => `${linkBase} ${isActive?linkActive:linkInactive}`} data-testid="nav-calibration"><Activity size={16}/> {t("nav.calibration")}</NavLink>
          <NavLink to="/customers" className={({isActive}) => `${linkBase} ${isActive?linkActive:linkInactive}`} data-testid="nav-customers"><Users size={16}/> {t("nav.customers")}</NavLink>

          <div className="pt-2 pb-1 px-3 label-tech text-[10px] text-slate-600">{t("nav.groupBusiness", "Business")}</div>
          <NavLink to="/analytics" className={({isActive}) => `${linkBase} ${isActive?linkActive:linkInactive}`} data-testid="nav-analytics"><BarChart3 size={16}/> {t("nav.analytics")}</NavLink>
          <NavLink to="/marketplace" className={({isActive}) => `${linkBase} ${isActive?linkActive:linkInactive}`} data-testid="nav-marketplace"><ShoppingCart size={16}/> {t("nav.marketplace")}</NavLink>
          <NavLink to="/machine-roi" className={({isActive}) => `${linkBase} ${isActive?linkActive:linkInactive}`} data-testid="nav-machine-roi"><Wrench size={16}/> {t("nav.machineRoi")}</NavLink>
          <NavLink to="/lost-opportunity" className={({isActive}) => `${linkBase} ${isActive?linkActive:linkInactive}`} data-testid="nav-lost-opportunity"><TrendingDown size={16}/> {t("nav.lostOpportunity", "Lost Opportunity")}</NavLink>
          <NavLink to="/billing" className={({isActive}) => `${linkBase} ${isActive?linkActive:linkInactive}`} data-testid="nav-billing"><CreditCard size={16}/> {t("nav.billing", "Piano & Fatturazione")}</NavLink>
          <NavLink to="/settings" className={({isActive}) => `${linkBase} ${isActive?linkActive:linkInactive}`} data-testid="nav-settings"><Settings size={16}/> {t("nav.settings", "Impostazioni")}</NavLink>
          {isAdmin && (
            <>
              <div className="pt-2 pb-1 px-3 label-tech text-[10px] text-amber-500">{t("nav.admin", "Admin")}</div>
              <NavLink to="/admin" className={({isActive}) => `${linkBase} ${isActive?"bg-amber-500/10 text-amber-300 border-l-2 border-amber-500":linkInactive}`} data-testid="nav-admin"><ShieldAlert size={16}/> {t("nav.admin", "Admin Console")}</NavLink>
            </>
          )}
        </nav>

        <div className="p-3 border-t border-slate-800 space-y-2">
          <TierBadge tier={tier} usage={usage} limits={limits} loading={tierLoading}/>
          <div className="flex items-center gap-1">
            <LanguageSwitcher/>
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : theme === "light" ? "system" : "dark")}
              className="p-2 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-800"
              title={`${t("nav.themeToggle")}: ${theme === "system" ? `auto (${resolvedTheme})` : theme}`}
              data-testid="theme-toggle"
            >
              {theme === "system" ? <Monitor size={14}/> : (theme === "dark" ? <Sun size={14}/> : <Moon size={14}/>)}
            </button>
          </div>
          <div className="px-3 py-2 rounded-md bg-slate-900 border border-slate-800">
            <div className="label-tech mb-1">{t("nav.operator")}</div>
            <div className="text-sm text-slate-100 font-medium truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.company_name || user?.email}</div>
          </div>
          <button onClick={handleLogout} className={`${linkBase} ${linkInactive} w-full`} data-testid="logout-btn"><LogOut size={16}/> {t("nav.logout")}</button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-auto">
        <div className="lg:hidden sticky top-0 z-20 flex items-center gap-3 px-4 py-3 border-b border-slate-800 bg-slate-950/95 backdrop-blur">
          <button
            type="button"
            onClick={() => setNavOpen((v) => !v)}
            className="p-2 -ml-2 rounded-md text-slate-300 hover:text-slate-100 hover:bg-slate-800"
            aria-label="toggle navigation"
            data-testid="mobile-nav-toggle"
          >
            {navOpen ? <X size={18}/> : <Menu size={18}/>}
          </button>
          <BrandIcon size={24} />
          <span className="font-display font-bold text-slate-100 text-sm">{t("app.name")}</span>
        </div>
        <BetaBanner/>
        <VerifyEmailBanner/>
        <CompleteProfileBanner/>
        {children}
      </main>
    </div>
  );
}
